import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faGraduationCap } from '@fortawesome/free-solid-svg-icons';
import { Pie, Line, Funnel, Column } from '@ant-design/plots';
import EscopoBadge from './EscopoBadge';
import InfoTip from './InfoTip';

/**
 * Seção Acadêmico do Dashboard Admin (V2).
 * Recebe `data` no shape retornado por GET /api/dashboard-admin/academico.
 *
 * Todos os gráficos aqui ignoram o filtro de PERÍODO (representam
 * histórico total ou janela fixa de 12 meses) e respondem só ao filtro
 * de extensão. Por isso a tag de escopo padrão é 'extensao' — exceto
 * quando há janela fixa, aí usamos 'fixo'.
 */

const STATUS_COLORS = {
  Ativa: '#22c55e',
  Concluida: '#3b82f6',
  Trancada: '#eab308',
  Cancelada: '#ef4444',
};

const Card = ({ titulo, escopo, escopoLabel, info, children }) => (
  <div className="bg-white rounded-lg shadow flex flex-col">
    <div className="px-4 py-3 border-b flex items-center gap-2 flex-wrap">
      <span className="font-semibold text-gray-900 text-sm">{titulo}</span>
      {escopo && <EscopoBadge escopo={escopo} label={escopoLabel} />}
      {info && <InfoTip text={info} position="bottom" />}
    </div>
    <div className="p-4 flex-1 min-h-[260px]">{children}</div>
  </div>
);

const Loading = () => (
  <div className="flex items-center justify-center h-full text-gray-400 text-sm">
    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />Carregando…
  </div>
);

const Empty = ({ msg = 'Sem dados no período' }) => (
  <div className="flex items-center justify-center h-full text-gray-400 text-sm">{msg}</div>
);

const SecaoAcademico = ({ data, loading }) => {
  if (loading) {
    return (
      <div>
        <div className="flex items-center gap-2 mb-3">
          <FontAwesomeIcon icon={faGraduationCap} className="text-indigo-600" />
          <h2 className="font-semibold text-gray-900">Acadêmico</h2>
        </div>
        <div className="bg-white rounded-lg shadow p-12"><Loading /></div>
      </div>
    );
  }
  if (!data) return null;

  // 1. Donut — matrículas por status
  const donutData = data.matriculas_por_status?.map(r => ({ tipo: r.status, valor: r.qtd })) || [];
  const donutConfig = {
    data: donutData,
    angleField: 'valor',
    colorField: 'tipo',
    innerRadius: 0.6,
    legend: { position: 'bottom' },
    label: { text: 'valor', style: { fontSize: 11 } },
    scale: { color: { range: donutData.map(d => STATUS_COLORS[d.tipo] || '#9ca3af') } },
    height: 240,
  };

  // 2. Linha — evolução de matrículas 12 meses + projeção 3 meses
  const evolucaoReal = (data.evolucao_matriculas_12m || []).map(r => ({ mes: r.mes, qtd: r.qtd, tipo: 'Real' }));
  const evolucaoProj = (data.projecao_matriculas_3m || []).map(r => ({ mes: r.mes, qtd: r.qtd, tipo: 'Projeção' }));
  // Conectar a projeção ao último ponto real para evitar gap visual
  const ponteProj = evolucaoReal.length > 0 && evolucaoProj.length > 0
    ? [{ ...evolucaoReal[evolucaoReal.length - 1], tipo: 'Projeção' }, ...evolucaoProj]
    : evolucaoProj;
  const evolucaoData = [...evolucaoReal, ...ponteProj];
  const lineConfig = {
    data: evolucaoData,
    xField: 'mes',
    yField: 'qtd',
    colorField: 'tipo',
    seriesField: 'tipo',
    point: { shapeField: 'circle', sizeField: 3 },
    smooth: true,
    legend: { position: 'top' },
    scale: { color: { range: ['#4f46e5', '#a5b4fc'] } },
    style: { lineDash: (d) => d.tipo === 'Projeção' ? [4, 4] : null },
    height: 240,
  };

  // 3. Barra horizontal — top cursos.
  // Converter `ativas` para Number garante que o chart aplique escala
  // numérica (caso contrário, o MySQL pode devolver strings e o Bar
  // trata como categoria, invertendo os eixos visualmente).
  // Ordenar desc para Top apareceram no topo.
  const cursosData = (data.top_cursos || [])
    .map(r => ({ curso: r.nome, ativas: Number(r.ativas) || 0 }))
    .sort((a, b) => b.ativas - a.ativas);
  const barConfig = {
    data: cursosData,
    xField: 'curso',
    yField: 'ativas',
    height: 280,
    label: { position: 'top', style: { fontSize: 11 } },
    axis: {
      x: { labelAutoRotate: true, labelFormatter: (v) => v && v.length > 14 ? v.slice(0, 12) + '…' : v },
      y: { labelFormatter: (v) => new Intl.NumberFormat('pt-BR').format(v) },
    },
    tooltip: { items: [{ field: 'ativas', name: 'Matrículas ativas', valueFormatter: (v) => new Intl.NumberFormat('pt-BR').format(v) }] },
  };

  // 4. Funil acadêmico — cada etapa carrega uma descrição usada no tooltip
  // para explicar ao gestor de onde veio o número.
  const fnum = (n) => new Intl.NumberFormat('pt-BR').format(n || 0);
  const totalCad = data.funil.cadastrados || 0;
  const pctSobreCadastrados = (n) => totalCad > 0 ? ((n / totalCad) * 100).toFixed(1) + '%' : '0%';
  const funilData = [
    {
      etapa: 'Cadastrados',
      valor: data.funil.cadastrados,
      descricao: 'Todas as pessoas registradas no sistema com o tipo "Aluno", independentemente de terem matrícula ou não.',
    },
    {
      etapa: 'Com matrícula',
      valor: data.funil.com_matricula,
      descricao: 'Alunos que possuem pelo menos uma matrícula em qualquer status (Ativa, Trancada, Concluída ou Cancelada). Mede quantos cadastros chegaram a se matricular ao menos uma vez.',
    },
    {
      etapa: 'Cursando (Ativa)',
      valor: data.funil.cursando,
      descricao: 'Alunos com pelo menos uma matrícula no status "Ativa" no momento. É o público que está estudando agora.',
    },
    {
      etapa: 'Concluído',
      valor: data.funil.concluido,
      descricao: 'Alunos com pelo menos uma matrícula no status "Concluída". Mede quantos formaram em algum curso (a mesma pessoa pode também estar em outras etapas se tiver mais de uma matrícula).',
    },
  ];
  const funilConfig = {
    data: funilData,
    xField: 'etapa',
    yField: 'valor',
    legend: false,
    height: 240,
    label: { position: 'inside', style: { fill: '#fff', fontSize: 12 } },
    // Tooltip mostra valor e %; a explicação completa aparece em uma
    // legenda abaixo (texto longo em tooltip do antv-charts é truncado).
    tooltip: {
      title: (d) => d.etapa,
      items: [
        { name: 'Quantidade', field: 'valor', valueFormatter: (v) => fnum(v) },
        { name: '% sobre Cadastrados', channel: 'y', valueFormatter: (v) => pctSobreCadastrados(v) },
      ],
    },
  };

  // 5. Documentos por status (Column)
  const docsData = data.documentos_por_status?.map(r => ({ status: r.status, qtd: r.qtd })) || [];
  const docsConfig = {
    data: docsData,
    xField: 'status',
    yField: 'qtd',
    height: 240,
    label: { position: 'top', style: { fontSize: 11 } },
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <FontAwesomeIcon icon={faGraduationCap} className="text-indigo-600" />
        <h2 className="font-semibold text-gray-900">Acadêmico</h2>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        <Card titulo="Matrículas por status" escopo="extensao" escopoLabel="histórico total"
          info="Distribuição de TODAS as matrículas (histórico completo) por status: Ativa, Concluida, Trancada, Cancelada. Cada matrícula conta uma vez. Filtro de extensão se aplica.">
          {donutData.length ? <Pie {...donutConfig} /> : <Empty />}
        </Card>
        <Card titulo="Evolução de matrículas + projeção" escopo="fixo" escopoLabel="últimos 12 meses + 3m"
          info="Matrículas agrupadas por mês de matrícula nos últimos 12 meses (linha sólida) + projeção dos próximos 3 meses (linha pontilhada). Projeção usa média móvel dos 3 últimos meses. Filtro de extensão se aplica; filtro de período NÃO se aplica (a janela é sempre 12 meses).">
          {evolucaoData.length ? <Line {...lineConfig} /> : <Empty />}
        </Card>
        <Card titulo="Top 10 cursos por matrículas ativas" escopo="extensao" escopoLabel="histórico total"
          info="Os 10 cursos com mais matrículas no status 'Ativa' no momento. Histórico total (não considera período). Filtro de extensão se aplica.">
          {cursosData.length ? <Column {...barConfig} /> : <Empty />}
        </Card>
        <Card titulo="Funil acadêmico" escopo="extensao" escopoLabel="histórico total"
          info="Etapas: Cadastrados (todas as pessoas com dtype=Aluno) → Com matrícula (têm pelo menos uma matrícula em qualquer status) → Cursando (pelo menos uma matrícula Ativa) → Concluído (pelo menos uma matrícula Concluida). A explicação detalhada de cada etapa aparece abaixo do gráfico.">
          {funilData.some(d => d.valor > 0) ? (
            <div className="flex flex-col h-full">
              <Funnel {...funilConfig} />
              {/* Legenda explicativa fixa — o tooltip do antv trunca texto longo */}
              <details className="mt-3 text-xs text-gray-600 border-t pt-2">
                <summary className="cursor-pointer text-gray-700 font-medium select-none">
                  O que cada etapa significa
                </summary>
                <ul className="mt-2 space-y-1.5">
                  {funilData.map(d => (
                    <li key={d.etapa} className="leading-snug">
                      <span className="font-semibold text-gray-800">{d.etapa}</span>
                      <span className="text-gray-500"> ({fnum(d.valor)} · {pctSobreCadastrados(d.valor)}):</span>{' '}
                      <span>{d.descricao}</span>
                    </li>
                  ))}
                </ul>
              </details>
            </div>
          ) : <Empty />}
        </Card>
        <Card titulo="Documentos por status" escopo="extensao" escopoLabel="histórico total"
          info="Documentos dos alunos agrupados por status: pendente, aguardando_revisao, aprovado, rejeitado. Todo o histórico. Filtro de extensão se aplica (considera apenas docs de alunos das extensões selecionadas).">
          {docsData.length ? <Column {...docsConfig} /> : <Empty msg="Nenhum documento" />}
        </Card>
      </div>
    </div>
  );
};

export default SecaoAcademico;
